/**
 * Featured App Card Component - Reusable card template for displaying apps
 */

import { useState, useEffect, useRef } from 'preact/hooks';
import { Link, useLocation } from 'wouter-preact';
import type { AppData } from '../globals/firebase';
import { useUser } from '../contexts/UserContext';
import { callFunction, db } from '../globals/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { AuthModal } from './AuthModal';
import { NoLauncherModal } from './NoLauncherModal';

// Helper to detect mobile devices
const isMobileDevice = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    || navigator.maxTouchPoints > 1;
};

interface FeaturedAppCardProps {
  app: AppData;
  hoverBorder?: boolean;
  linkTo?: string;
  className?: string;
}

export function FeaturedAppCard({
  app,
  hoverBorder = false,
  linkTo,
  className = '',
}: FeaturedAppCardProps) {
  const [, setLocation] = useLocation();
  const { user, isAuthenticated, installApp, isAppInstalled, isAppEntitled, getInstalledVersion, installedApps } = useUser();

  const [installing, setInstalling] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [installedVersion, setInstalledVersion] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingInstallAppId, setPendingInstallAppId] = useState<string | null>(null);
  const [showNoLauncherModal, setShowNoLauncherModal] = useState(false);
  const [launching, setLaunching] = useState(false);
  const launchCleanupRef = useRef<(() => void) | null>(null);

  const appId = app.id;
  const isPurchased = !!(appId && isAppEntitled(appId));
  const isInstalled = !!(appId && isAppInstalled(appId));
  const isFree = app.price === 0;
  const needsUpdate = !!(isInstalled && app && installedVersion && installedVersion !== app.version);

  // Load installed version when app or installed status changes
  useEffect(() => {
    if (appId && installedApps.includes(appId)) {
      getInstalledVersion(appId).then(version => {
        setInstalledVersion(version);
      });
    } else {
      setInstalledVersion(null);
    }
  }, [appId, installedApps, getInstalledVersion]);

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      setLocation('/auth');
      return;
    }

    setPurchasing(true);
    try {
      const createCheckout = callFunction<{ appId: string }, { url: string }>('createCheckoutSession');
      const result = await createCheckout({ appId });

      // Redirect to Stripe Checkout
      window.location.href = result.data.url;
    } catch (err: any) {
      console.error('Error creating checkout:', err);
    } finally {
      setPurchasing(false);
    }
  };

  const handleInstall = async () => {
    if (!isAuthenticated) {
      // Show auth modal instead of redirecting
      setPendingInstallAppId(appId);
      setShowAuthModal(true);
      return;
    }

    await performInstall(appId);
  };

  const performInstall = async (targetAppId: string) => {
    setInstalling(true);
    try {
      // const trackDownload = callFunction<{ appId: string }, { success: boolean }>('trackDownload');
      // await trackDownload({ appId: targetAppId });

      // Use UserContext method to install app
      await installApp(targetAppId, app?.version || 'latest', app.name, app.iconUrl);

      // Refresh installed version
      if (isAppInstalled(targetAppId)) {
        const version = await getInstalledVersion(targetAppId);
        setInstalledVersion(version);
      }

      // Open nitros:// protocol to trigger launcher (launch handles install + open)
      // window.location.href = `nitros://launch/${targetAppId}`;
    } catch (err: any) {
      console.error('Error installing:', err);
    } finally {
      setInstalling(false);
    }
  };

  const handleAuthSuccess = async () => {
    // User authenticated, proceed with installation
    if (pendingInstallAppId) {
      setShowAuthModal(false);
      await performInstall(pendingInstallAppId);
      setPendingInstallAppId(null);
    }
  };

  const handleAuthModalClose = () => {
    setShowAuthModal(false);
    setPendingInstallAppId(null);
  };

  const handleLaunch = () => {
    // Check if user is on mobile
    if (isMobileDevice()) {
      alert('You can only launch apps from the store on a machine with the Nitros Launcher installed.');
      return;
    }

    // Prevent multiple rapid clicks
    if (launching) return;

    const userId = user.user?.uid;
    if (!userId || !appId) return;

    setLaunching(true);

    // Clean up any previous launch attempt
    if (launchCleanupRef.current) {
      launchCleanupRef.current();
      launchCleanupRef.current = null;
    }

    // Set up listener for lastLaunchedAt changes
    const appDocRef = doc(db, 'users', userId, 'installedApps', appId);
    let initialLastLaunchedAt: number | null = null;
    let hasDetectedLaunch = false;

    const unsubscribe = onSnapshot(appDocRef, (snapshot) => {
      if (!snapshot.exists()) return;

      const data = snapshot.data();
      const lastLaunchedAt = data.lastLaunchedAt?.toMillis?.() ?? null;

      // On first snapshot, record the initial value
      if (initialLastLaunchedAt === null) {
        initialLastLaunchedAt = lastLaunchedAt ?? 0;
        return;
      }

      // Check if lastLaunchedAt was updated (newer than initial)
      if (lastLaunchedAt && lastLaunchedAt > initialLastLaunchedAt) {
        hasDetectedLaunch = true;
        cleanup();
        setLaunching(false);
        // Launch successful - no need to show anything
      }
    });

    // Set up 5 second timeout
    const timeoutId = setTimeout(() => {
      if (!hasDetectedLaunch) {
        cleanup();
        setLaunching(false);
        setShowNoLauncherModal(true);
      }
    }, 5000);

    // Cleanup function to unsubscribe and clear timeout
    const cleanup = () => {
      unsubscribe();
      clearTimeout(timeoutId);
      launchCleanupRef.current = null;
    };

    launchCleanupRef.current = cleanup;

    // Trigger the protocol
    window.location.href = `nitros://launch/${appId}`;
  };

  const formatPrice = (price: number, currency: string) => {
    if (price === 0) return 'Free';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(price / 100);
  };

  // Render action button based on app state
  const renderActionButton = () => {
    const handleButtonClick = (e: MouseEvent, handler: () => void) => {
      e.preventDefault();
      e.stopPropagation();
      handler();
    };

    if (isInstalled) {
      if (needsUpdate) {
        return (
          <button
            class="bg-blue-600 hover:bg-blue-700 tracking-wider text-white font-semibold py-2 px-6 rounded-full transition-colors duration-200 shadow-lg shadow-2xl whitespace-nowrap disabled:bg-blue-400 disabled:cursor-not-allowed"
            onClick={(e) => handleButtonClick(e, handleInstall)}
            disabled={installing}
          >
            {installing ? (
              <span class="flex items-center gap-2">
                <span class="spinner w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Updating
              </span>
            ) : (
              'Update'
            )}
          </button>
        );
      } else {
        return (
          <button
            class="bg-blue-600 hover:bg-blue-700 tracking-wider text-white font-semibold py-2 px-6 rounded-full transition-colors duration-200 shadow-lg shadow-2xl whitespace-nowrap disabled:bg-blue-400 disabled:cursor-not-allowed"
            onClick={(e) => handleButtonClick(e, handleLaunch)}
            disabled={launching}
          >
            {launching ? (
              <span class="flex items-center gap-2">
                <span class="spinner w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Launching
              </span>
            ) : (
              'Launch'
            )}
          </button>
        );
      }
    } else if (isFree || isPurchased) {
      return (
        <button
          class="bg-blue-600 hover:bg-blue-700 tracking-wider text-white font-semibold py-2 px-6 rounded-full transition-colors duration-200 shadow-lg shadow-2xl whitespace-nowrap disabled:bg-blue-400 disabled:cursor-not-allowed"
          onClick={(e) => handleButtonClick(e, handleInstall)}
          disabled={installing}
        >
          {installing ? (
            <span class="flex items-center gap-2">
              <span class="spinner w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Installing
            </span>
          ) : (
            'Install'
          )}
        </button>
      );
    } else {
      return (
        <button
          class="bg-neutral-700 hover:bg-neutral-600 tracking-wider text-neutral-300 hover:text-white font-semibold py-2 px-6 rounded-full transition-colors duration-200 shadow-lg shadow-2xl whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={(e) => handleButtonClick(e, handlePurchase)}
          disabled={purchasing}
        >
          {purchasing ? (
            <span class="flex items-center gap-2">
              <span class="spinner w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Processing
            </span>
          ) : (
            <>Buy for {formatPrice(app.price, app.currency)}</>
          )}
        </button>
      );
    }
  };

  const cardContent = (
    <div class={`relative overflow-hidden rounded-3xl bg-neutral-900/50 border border-neutral-800 ${hoverBorder ? 'hover:border-indigo-500' : ''} transition-all duration-300 ${linkTo ? 'cursor-pointer' : ''} ${className}`}>
      {/* Blurred background */}
      <div class="absolute inset-0">
        {app.iconUrl ? (
          <img
            src={app.iconUrl}
            alt=""
            class="w-full h-full object-cover scale-150 blur-3xl opacity-30"
          />
        ) : (
          <div class="w-full h-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 opacity-20" />
        )}
      </div>
      {/* Overlay for better text readability */}
      <div class="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/20 to-transparent" />
      {/* Content */}
      <div class="relative z-10 p-6 h-full row aic gap-5">
        {/* App Icon */}
        <div class="flex-shrink-0 w-1/4 rounded-2xl overflow-hidden shadow-xl" style={{ 'corner-shape': 'squircle' }}>
          {app.iconUrl ? (
            <img src={app.iconUrl} alt={app.name} class="w-full h-full object-cover" />
          ) : (
            <div class="w-full h-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white flex items-center justify-center text-2xl font-black text-opacity-60">
              {app.name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        {/* App Details */}
        <div class="flex-1 min-w-0">
          <h3 class={`text-xl font-medium text-white tracking-wide line-clamp-1`}>{app.name}</h3>
          <p class={`text-neutral-300 text-sm line-clamp-1 leading-none mt-0.5`}>{app.description}</p>
          <span class="text-[0.62rem] text-neutral-400 font-medium font-mono">
            {app.githubUsername}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div class="relative w-full">
      {linkTo ? (
        <Link to={linkTo} class="block text-inherit no-underline">
          {cardContent}
        </Link>
      ) : (
        cardContent
      )}
      {/* Action Button */}
      <div class="absolute -bottom-4 right-6 z-10">
        {renderActionButton()}
      </div>
      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={handleAuthModalClose}
        onAuthSuccess={handleAuthSuccess}
      />
      {/* No Launcher Modal */}
      <NoLauncherModal
        isOpen={showNoLauncherModal}
        onClose={() => setShowNoLauncherModal(false)}
      />
    </div>
  );
}
